<template>
  <div id="dashboard">
    <WaveBg />

    <template v-if="learningTypeList.length > 0">
      <!-- Learning type -->
      <div class="select-screen">
        <div class="select-box">
          <h1 class="list-title">LEARNING TYPE</h1>
          <p class="list-sub-title">Please select a service</p>
          <ul class="select-list">
            <li
              v-for="(learningType, index) in learningTypeList"
              v-bind:key="index"
              :class="{
                'list-column': true,
                clickable: learningType.available,
                available: learningType.available,
              }"
              @click="goLearningType(learningType)"
              @keypress.enter="goLearningType(learningType)"
            >
              <div class="transition-wrap" />
              <div class="gradient-wrap">
                <div class="card-content">
                  <inline-svg :src="$path.resolveImage(learningType.image)" width="45%" />
                  <div class="card-name">{{ learningType.name }}</div>
                  <div class="card-summary">{{ learningType.summary }}</div>
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>
      <!-- Service end -->

      <!-- Utility -->
      <div class="select-screen">
        <div class="select-box">
          <h1 class="list-title">UTILITY</h1>
          <p class="list-sub-title">Please select a widget</p>
          <ul class="select-list">
            <li
              v-for="(utility, index) in utilityList"
              v-bind:key="index"
              :class="{
                'list-column': true,
                clickable: true,
                available: true,
              }"
              @click="openUtility(utility)"
              @keypress.enter="openUtility(utility)"
            >
              <div class="transition-wrap" />
              <div class="gradient-wrap">
                <div class="card-content">
                  <inline-svg :src="$path.resolveImage(utility.image)" height="45%" />
                  <div class="card-name">{{ utility.title }}</div>
                  <div class="card-summary">{{ utility.summary }}</div>
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>
      <!-- Utility end -->

      <ScrollCardFade id="nav-box" container-selector="#dashboard" screen-group-selector=".select-screen" />
    </template>

    <!-- Logout button -->
    <div class="logout-btn clickable" @click="logout" @keypress.enter="logout">
      <inline-svg class="logout-img" :src="$assets.svg['icon-logout-btn']" width="32" height="32" />
      <p>Logout</p>
    </div>
  </div>
</template>

<script setup>
import { ref, onBeforeMount } from 'vue';
import { useRouter } from 'vue-router';

import $assets from '@/assets';
import ScrollCardFade from '@/components/common/ScrollCardFade.vue';
import WaveBg from '@/components/common/WaveBackground.vue';
import LearningTypeService from '@/services/learning-type';
import UtilityService from '@/services/utility';
import $path from '@/utils/path';
import $session from '@/utils/session';

const $router = useRouter();
const learningTypeList = ref([]);
const utilityList = ref([]);

onBeforeMount(async () => {
  const learningTypeResults = await LearningTypeService.getLearningTypes();
  const utilityResults = await UtilityService.getUtilities();

  learningTypeList.value = learningTypeResults.records
    .filter((r) => Number(r.is_ui_gate) === 1)
    .map((r) => ({ ...r, available: r.learning_type_status === 'available' }));

  utilityList.value = utilityResults.records;
});

function openUtility(utility) {
  if (utility.utility_type === 'route') {
    $router.push({ path: utility.utility_path });
  } else if (utility.utility_type === 'file') {
    // console.log($path.resolveFile(utility.utility_path));
    window.open($path.resolveFile(utility.utility_path), '_blank');
  }
}

function goLearningType(learningType) {
  if (learningType.learning_type_status !== 'available') {
    return;
  }

  const isEvent = learningType.learning_type_path === 'eventHome';

  $router.push({
    name: learningType.learning_type_path,
    params: isEvent ? {} : { learningTypeId: learningType.id },
  });
}

function logout() {
  $session.logout();
}
</script>

<script>
export default {
  name: 'DashboardPage',
};
</script>

<style scoped>
#dashboard {
  width: 100%;
  height: 100%;
}

/** ===================== */
/** Scrolling screen */
.select-screen {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  position: relative;
  box-sizing: border-box;
  width: 100%;
  min-height: 100%;
  padding: 4vmin;
}

/** Scrolling screen card */
.select-box {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;

  box-sizing: border-box;
  box-shadow: 0 0 1em 0 #6d6e71;
  border-radius: 0.8em;
  width: 100%;
  padding: 2em;
  background: #ffffff;
  text-align: center;
}

/** ===================== */
/** Screen elements */
h1.list-title {
  display: inline-block;
  margin: 0;
  padding: 0.2em 2em;
  border-bottom: 1px solid #939598;
}
p.list-sub-title {
  margin: 0.2em 0;
  font-size: 1.2em;
}
ul.select-list {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;

  margin-top: 2em;
  width: 100%;
}

/** ===================== */
/** Learning card */
/** Cards in list */
.list-column {
  position: relative;
  box-sizing: border-box;
  margin: 0.5em 2em;
  width: 100%;
  height: 65vw;
  border-radius: 0.8em;
  padding: 0.25em;
  background: linear-gradient(to right, #66cc99, #3399cc);
}
.list-column .transition-wrap {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);

  width: 101%;
  height: 101%;
  background: #fff;
  transition: opacity 0.3s;
}
.list-column.available:hover .transition-wrap {
  opacity: 0;
}
.list-column .gradient-wrap {
  display: flex;
  align-items: center;

  position: relative;
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  border-radius: 0.6em;
  padding: 0 0.5em;
  background: #ffffff;
  color: #d3d3d3;
  transition: color 0.3s;
}
.list-column.available .gradient-wrap {
  color: #808285;
}

.list-column .gradient-wrap .card-content {
  display: flex;
  flex-flow: column;
  justify-content: flex-start;
  align-items: center;

  width: 100%;
  height: 80%;
}
.list-column.available:hover .card-content {
  color: #8ac640;
}

.list-column .card-content svg {
  fill: #d3d3d3;
  transition: fill 0.3s;
}
.list-column.available .card-content svg {
  fill: #6d6e71;
}
.list-column.available:hover .card-content svg {
  fill: #8ac640;
}

.list-column .card-content .card-name {
  margin: 0.6em 0;
  font-size: 1.2em;
}

/** Logout button */
.logout-btn {
  display: flex;
  align-items: center;

  position: fixed;
  left: 3vmin;
  top: 4vmin;
  border-radius: 0.8em;
  padding: 0.8em;
}
.logout-btn svg {
  fill: #333;
}
.logout-btn p {
  display: none;
  margin: 0 0 0 0.8em;
  color: #333;
}

/** Landscape mobile */
@media only screen and (orientation: landscape) and (max-height: 600px) {
  .select-box {
    padding: 1em;
  }

  ul.select-list {
    justify-content: space-evenly;
    margin-top: 1em;
  }

  .list-column {
    margin: 0;
    width: 50vh;
    height: 56vh;
  }

  .list-column .card-content .card-name {
    font-size: 1.1em;
  }
  .list-column .card-content .card-summary {
    font-size: 0.9em;
  }
}

/** Larger devices */
@media only screen and (min-width: 601px) and (min-height: 601px) {
  #dashboard {
    display: flex;
    flex-direction: column;
    align-items: center;
    overflow-y: auto;
  }

  .select-screen {
    max-width: 60em;
    padding: 2vmin;
  }

  .select-box {
    flex-grow: initial;
    padding: 3em;
  }

  h1.list-title {
    margin-top: 1em;
  }

  ul.select-list {
    flex-wrap: nowrap;
    justify-content: center;
    margin-top: 2em;
  }

  .list-column {
    margin: 0;
    width: 14em;
    height: 16em;
  }
  .list-column:not(:last-child) {
    margin-right: 2em;
  }

  .logout-btn {
    top: initial;
    left: 5vmin;
    bottom: 5vmin;
  }
  .logout-btn:hover {
    box-shadow: 0px 0px 0.8em 0.1em rgba(000, 000, 000, 0.3);
    transition: 0.3s;
  }
  .logout-btn svg {
    fill: #fff;
  }
  .logout-btn p {
    display: block;
    color: #fff;
    font-size: 1.1em;
  }
}
</style>
